import { ReactNode } from "react";

import styled from "styled-components/macro";

interface SettingRowProps {
  label: ReactNode;
  /** id of the control this row labels — wires up `<label htmlFor>` for accessibility. */
  htmlFor?: string;
  /** Explicit override; defaults to a colon for non-blank string labels. */
  colon?: boolean;
  /** Override the label column width (default "150px"). */
  labelWidth?: string;
  children: ReactNode;
}

/**
 * Shared two-column layout (label | control) for every screenshot settings row.
 * Owning the grid here keeps all rows aligned to the same 150px label column and
 * associates each label with its control via `htmlFor`/`id`.
 */
export function SettingRow({
  label,
  htmlFor,
  colon,
  labelWidth,
  children
}: SettingRowProps) {
  const isBlankLabel = typeof label === "string" && label.trim() === "";
  const showColon = colon ?? !isBlankLabel;

  return (
    <ItemRow $labelWidth={labelWidth}>
      <LabelCol>
        <label htmlFor={htmlFor}>
          {label}
          {showColon ? ":" : ""}
        </label>
      </LabelCol>
      <ControlCol>{children}</ControlCol>
    </ItemRow>
  );
}

const ItemRow = styled.div<{ $labelWidth?: string }>`
  display: grid;
  grid-template-columns: ${({ $labelWidth }) =>
    $labelWidth ? `${$labelWidth} 1fr` : "minmax(max-content, 1fr) 1fr"};
  align-items: center;
  gap: 4px;
  margin-bottom: 5px;
`;

const LabelCol = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.85);
  min-height: 32px;
  line-height: 1.2;
  word-break: break-word;
  max-width: 200px;
`;

const ControlCol = styled.div`
  display: flex;
  justify-content: flex-end;
`;
